/** 工具结果语气：用于侧栏/工具行 outcome 配色，与全局 primary 解耦 */
export type ToolCallResultTone = "success" | "warning" | "neutral"

export interface ToolCall {
  tool: string
  label: string
  category: "feishu" | "search" | "data" | "ai" | "ppt" | "file" | "general"
  paramLabel?: string
  result?: string
  /** 可选；未设时由 ToolCallItem 按 result 文案粗判，或视为 neutral */
  resultTone?: ToolCallResultTone
  status: "pending" | "running" | "done" | "waiting"
  delay: number
}

export interface Skill {
  name: string
  description: string
  icon: string
  toolCalls: ToolCall[]
  status: "pending" | "running" | "done"
  delay: number
}

export interface AgentDetailStep {
  type: "thinking" | "planning" | "text" | "tool" | "result"
  content?: string
  skillRef?: number
  toolRef?: number
  /** Rich UI cards to render inline in sidebar after this step */
  richCards?: RichCard[]
  delay: number
}

export interface SubAgent {
  id: string
  name: string
  role: string
  description: string
  skills: Skill[]
  detailSteps: AgentDetailStep[]
  status: "pending" | "running" | "done"
  dependencies?: string[]
  delay: number
}

export interface MainToolCall {
  label: string
  paramLabel?: string
  result?: string
  delay: number
  duration: number
}

export interface AgentDemoStep {
  type: "user" | "agent-reply" | "agent-thinking" | "main-tools" | "subagents" | "summary" | "follow-up"
  content?: string
  contentAfter?: string
  mainTools?: MainToolCall[]
  subAgents?: SubAgent[]
  followUps?: string[]
  /** summary 类型：执行摘要条目列表 */
  summaryItems?: string[]
  /** summary 类型：底部提示文案 */
  summaryFooter?: string
  /** Rich UI cards rendered below the step content */
  richCards?: RichCard[]
  delay: number
}

// ── Rich Card types for demo visualization ──

export type RichCard =
  | { type: "deliverables"; title?: string; files: { name: string; type: string; size?: string }[] }
  | { type: "audio"; title: string; subtitle?: string; duration: string; format?: string }
  | { type: "images"; title?: string; images: { label: string; dimensions?: string; recommended?: boolean; colors?: [string, string]; src?: string }[] }
  | { type: "ppt"; filename: string; pageCount: number; slides: { title: string; bulletPoints?: string[]; gradient?: string }[] }
  | { type: "alert"; title?: string; items: { level: "danger" | "warning" | "safe"; label: string; value?: string; detail?: string }[] }
  | { type: "table"; title?: string; columns: { key: string; label: string; align?: "left" | "center" | "right" }[]; rows: Record<string, unknown>[] }
  | { type: "breakdown"; title?: string; total?: number; unit?: string; items: { label: string; count: number; color?: string }[] }
  | { type: "code"; title?: string; language?: string; code: string }

export const DEMO_STEPS: AgentDemoStep[] = [
  {
    type: "user",
    content: "帮我准备一个下周 AI 产品发布会方案，并制作一个 PPT，然后通过飞书发送给李总",
    delay: 0,
  },
  {
    type: "agent-reply",
    content: "好的，我来帮您完成这个任务。让我先确认一些关键信息。",
    delay: 1800,
  },
  {
    type: "main-tools",
    delay: 4500,
    mainTools: [
      { label: "查询日历安排", paramLabel: "下周可用时间段", result: "周三、周四下午可用", delay: 0, duration: 1800 },
      { label: "获取联系人信息", paramLabel: "李总 · 飞书 ID", result: "已确认", delay: 2200, duration: 1500 },
    ],
  },
  {
    type: "agent-thinking",
    content: "正在拆解任务并规划执行流程...",
    contentAfter: "任务规划完成，已分配 8 个专业 Agent 协同执行",
    delay: 10500,
  },
  {
    type: "subagents",
    delay: 13500,
    subAgents: [
      /* ── 1. 技能加载 ── */
      {
        id: "skill-env",
        name: "技能加载",
        role: "环境",
        description: "加载发布会相关技能包",
        status: "pending",
        delay: 0,
        skills: [
          {
            name: "SkillHub",
            icon: "skillhub",
            description: "技能包加载",
            status: "pending",
            delay: 200,
            toolCalls: [
              { tool: "SkillHub.search", label: "搜索技能市场", category: "search", paramLabel: "AI 发布会策划相关技能", result: "匹配 3 个技能包", status: "pending", delay: 300 },
              { tool: "SkillHub.load", label: "加载技能包", category: "general", paramLabel: "活动策划 / PPT 生成 / 数据分析", result: "技能包已就绪", status: "pending", delay: 1600 },
              { tool: "SkillHub.check", label: "检测飞书技能", category: "search", paramLabel: "飞书协作能力", result: "未安装，需补充", resultTone: "warning", status: "pending", delay: 2800 },
              { tool: "SkillHub.install", label: "安装飞书协作", category: "general", paramLabel: "飞书协作 v2.4", result: "安装完成", resultTone: "success", status: "pending", delay: 3600 },
            ],
          },
        ],
        detailSteps: [
          { type: "thinking", content: "复合任务——方案策划、PPT、飞书通讯，三种能力缺一不可。先看看当前环境有哪些技能包可用，缺的从市场动态加载。", delay: 200 },
          { type: "planning", content: "1. 在技能市场搜索匹配的技能包\n2. 加载所需技能到执行环境中\n3. 检查并补充缺失的通讯技能", delay: 1400 },
          { type: "tool", skillRef: 0, toolRef: 0, delay: 2200 },
          { type: "text", content: "在技能市场中搜索到了 3 个匹配的技能包：「活动策划」「PPT 生成」「数据分析」，正在加载到执行环境。", delay: 3800 },
          { type: "tool", skillRef: 0, toolRef: 1, delay: 5000 },
          { type: "text", content: "基础技能包加载完成。检测到任务中包含飞书发送需求，当前环境缺少飞书协作技能，需要补充安装。", delay: 7000 },
          { type: "tool", skillRef: 0, toolRef: 2, delay: 8200 },
          { type: "tool", skillRef: 0, toolRef: 3, delay: 9500 },
          { type: "result", content: "技能环境准备完毕。已加载 3 个基础技能包 + 补充安装「飞书协作 v2.4」技能包，共 15 个工具可供调用。", delay: 11000 },
        ],
      },
      /* ── 2. 数据采集 ── */
      {
        id: "data-collect",
        name: "数据采集",
        role: "数据",
        description: "获取产品和销售数据",
        status: "pending",
        delay: 2400,
        skills: [
          {
            name: "数据收集",
            icon: "database",
            description: "内部数据采集",
            status: "pending",
            delay: 200,
            toolCalls: [
              { tool: "ExcelReader.read", label: "读取销售报表", category: "data", paramLabel: "sales_data.xlsx", result: "156 行数据，Q1-Q4 趋势", status: "pending", delay: 400 },
              { tool: "Database.query", label: "查询产品库", category: "data", paramLabel: "AI 产品信息表", result: "12 款产品详情", status: "pending", delay: 2000 },
            ],
          },
        ],
        detailSteps: [
          { type: "thinking", content: "方案要有说服力，数据是关键。需要两类——销售趋势（特别是增长曲线）和产品矩阵。先从内部系统拉，这两块到手后亮点展示和演讲稿的素材就齐了。", delay: 200 },
          { type: "planning", content: "1. 从 Excel 报表获取 Q1-Q4 销售趋势\n2. 从产品数据库查询完整产品列表", delay: 1200 },
          { type: "tool", skillRef: 0, toolRef: 0, delay: 2000 },
          { type: "text", content: "报表数据已读取完成，注意到 Q4 销售额环比增长了 23%，这是一个非常好的亮点数据。接下来获取产品详情...", delay: 4800 },
          { type: "tool", skillRef: 0, toolRef: 1, delay: 6200 },
          { type: "text", content: "产品数据库中共有 12 款 AI 产品，其中 3 款为本季度新品，可以重点展示。", delay: 8500 },
          { type: "result", content: "数据采集完成。156 行销售数据（Q4 增长 23%，全年营收同比 +47%）+ 12 款产品完整信息（含 3 款新品），数据质量检查通过。", delay: 10000 },
        ],
      },
      /* ── 3. 行业调研 ── */
      {
        id: "industry-research",
        name: "行业调研",
        role: "研究",
        description: "收集行业案例与趋势",
        status: "pending",
        delay: 3600,
        skills: [
          {
            name: "调研分析",
            icon: "search",
            description: "行业趋势分析",
            status: "pending",
            delay: 200,
            toolCalls: [
              { tool: "WebSearch.search", label: "搜索行业案例", category: "search", paramLabel: "2025 AI 发布会最佳实践", result: "28 篇文章，5 个案例", status: "pending", delay: 500 },
              { tool: "TrendAnalyzer.analyze", label: "生成趋势报告", category: "ai", paramLabel: "分析发布会形式演变", result: "趋势报告已生成", status: "pending", delay: 2400 },
            ],
          },
        ],
        detailSteps: [
          { type: "thinking", content: "光有数据不够，还得看行业在怎么做。两个方向——头部公司近半年的 AI 发布会案例（形式、亮点、受众反馈），以及当下流行的互动形式。避免千篇一律，关键在于差异化。", delay: 200 },
          { type: "planning", content: "1. 全网搜索近期 AI 产品发布会案例\n2. 提取共性趋势并生成分析报告", delay: 1200 },
          { type: "tool", skillRef: 0, toolRef: 0, delay: 2000 },
          { type: "text", content: "搜索到了 28 篇相关文章和 5 个高质量案例，包括 OpenAI DevDay、Google I/O、微软 Build 等。正在提取关键信息并分析共性特征...", delay: 5000 },
          { type: "tool", skillRef: 0, toolRef: 1, delay: 6500 },
          { type: "text", content: "趋势报告已生成。值得注意的是，今年的 AI 发布会普遍在强调「可落地的场景化演示」而非单纯的功能列举，这对我们的方案设计很有参考价值。", delay: 9500 },
          { type: "result", content: "核心发现：沉浸式体验（出现率 85%）、实时 Live Demo（78%）和观众互动问答（72%）是三大主流元素。建议方案中设置至少 2 个现场演示环节。", delay: 11500 },
        ],
      },
      /* ── 4. 场地搜索 ── */
      {
        id: "venue-search",
        name: "场地搜索",
        role: "后勤",
        description: "筛选活动场地",
        status: "pending",
        delay: 5200,
        skills: [
          {
            name: "场地搜索",
            icon: "location",
            description: "活动场地筛选",
            status: "pending",
            delay: 200,
            toolCalls: [
              { tool: "WebSearch.search", label: "搜索活动场地", category: "search", paramLabel: "科技活动场地 200人", result: "3 个推荐场地", status: "pending", delay: 500 },
            ],
          },
        ],
        detailSteps: [
          { type: "thinking", content: "日历显示下周三或周四可用。场地硬条件：200 人容量、大屏 LED、稳定网络——Live Demo 断网就完了。再加上交通便利和科技氛围。预算先不设上限，给几个档位让用户选。", delay: 200 },
          { type: "planning", content: "1. 搜索满足条件的科技风格场地\n2. 对比价格、设施和交通，筛选 Top 3", delay: 1000 },
          { type: "tool", skillRef: 0, toolRef: 0, delay: 1800 },
          { type: "text", content: "搜索到 7 个候选场地，按综合评分排序后筛选出 3 个最优选项，覆盖了不同的预算区间。", delay: 4200 },
          { type: "result", content: "推荐 3 个场地：Innovation Hub（8k/天，200 人，距地铁 5 分钟）、Tech Park Center（12k/天，设备最全含 LED 墙）、Marina Bay Expo（25k/天，高端首选含专业灯光）。", delay: 5500 },
        ],
      },
      /* ── 5. 内部协调 ── */
      {
        id: "internal-comm",
        name: "内部协调",
        role: "协作",
        description: "获取演示素材",
        status: "pending",
        delay: 7000,
        skills: [
          {
            name: "飞书沟通",
            icon: "message",
            description: "团队协调",
            status: "pending",
            delay: 200,
            toolCalls: [
              { tool: "Feishu.send", label: "发消息给小张", category: "feishu", paramLabel: "请提供最新产品演示数据和截图", status: "pending", delay: 400 },
              { tool: "Feishu.wait", label: "等待回复", category: "feishu", paramLabel: "小张 输入中...", status: "pending", delay: 2200 },
              { tool: "Feishu.receive", label: "收到回复", category: "feishu", paramLabel: "小张", result: "数据已上传，但缺演示视频", status: "pending", delay: 5500 },
              { tool: "Feishu.send", label: "发消息给小王", category: "feishu", paramLabel: "麻烦帮忙补充产品演示视频", status: "pending", delay: 6800 },
              { tool: "Feishu.wait", label: "等待回复", category: "feishu", paramLabel: "小王 输入中...", status: "pending", delay: 8000 },
              { tool: "Feishu.receive", label: "收到回复", category: "feishu", paramLabel: "小王", result: "3 分钟演示视频已上传共享盘", status: "pending", delay: 11500 },
            ],
          },
        ],
        detailSteps: [
          { type: "thinking", content: "现场演示是发布会最出彩的环节，但手上没有现成素材。先找产品组小张要截图和数据，有演示视频更好。同事可能在忙，先发消息过去，回复后再评估素材够不够用。", delay: 200 },
          { type: "planning", content: "1. 联系小张获取产品演示数据\n2. 检查素材完整性\n3. 如有缺失，协调其他同事补充", delay: 1400 },
          { type: "text", content: "先联系产品组的小张，她负责产品演示相关的数据和截图。", delay: 2200 },
          { type: "tool", skillRef: 0, toolRef: 0, delay: 2800 },
          { type: "tool", skillRef: 0, toolRef: 1, delay: 5200 },
          { type: "tool", skillRef: 0, toolRef: 2, delay: 9000 },
          { type: "text", content: "小张回复了，数据和截图都有，但演示视频还没录。数据组的小王之前做过类似的视频，联系他看看。", delay: 11000 },
          { type: "tool", skillRef: 0, toolRef: 3, delay: 13000 },
          { type: "tool", skillRef: 0, toolRef: 4, delay: 15000 },
          { type: "tool", skillRef: 0, toolRef: 5, delay: 19000 },
          { type: "text", content: "小王手上刚好有一版之前录制的演示视频，已经上传到共享盘了。", delay: 21000 },
          { type: "result", content: "素材收集完成。来自小张的产品截图（12 个功能点）+ 来自小王的 3 分钟演示视频均已就绪，可直接用于 PPT 制作和现场演示。", delay: 23000 },
        ],
      },
      /* ── 6. 方案生成 ── */
      {
        id: "plan-gen",
        name: "方案生成",
        role: "整合",
        description: "生成完整方案",
        status: "pending",
        dependencies: ["data-collect", "industry-research", "venue-search", "internal-comm"],
        delay: 18000,
        skills: [
          {
            name: "AI 写作",
            icon: "brain",
            description: "方案生成",
            status: "pending",
            delay: 300,
            toolCalls: [
              { tool: "AIWriter.generate", label: "生成方案文档", category: "ai", paramLabel: "整合数据 + 调研 + 素材", result: "《发布会方案》18 页", status: "pending", delay: 600 },
            ],
          },
        ],
        detailSteps: [
          { type: "thinking", content: "上游产出全部到齐了——156 行销售数据（Q4 增长是亮点）、5 个行业案例、3 个场地、演示素材。现在是整合环节，把碎片拼成完整方案。结构上五大版块：目标、议程、场地、预算、分工。", delay: 200 },
          { type: "planning", content: "1. 汇总 4 个 Agent 的产出物\n2. 生成结构化方案文档\n3. 校验内容一致性和格式规范", delay: 1500 },
          { type: "text", content: "开始整合各方数据，以「Q4 增长 23%」作为方案开篇的核心亮点数据...", delay: 2800 },
          { type: "tool", skillRef: 0, toolRef: 0, delay: 3500 },
          { type: "text", content: "方案已生成，包含 5 个核心章节。考虑到行业调研的建议，我在议程中特别安排了 2 个 Live Demo 环节和 1 个观众互动问答环节。", delay: 7000 },
          { type: "result", content: "《AI 产品发布会策划方案》生成完毕，共 18 页。涵盖：活动目标与主题、议程流程（含 3 个演示环节）、场地对比推荐、预算明细（15-30 万三档方案）、团队分工表。", delay: 8500 },
        ],
      },
      /* ���─ 7. PPT 制作 ── */
      {
        id: "ppt-gen",
        name: "PPT 制作",
        role: "设计",
        description: "制作演示文稿",
        status: "pending",
        dependencies: ["plan-gen"],
        delay: 24000,
        skills: [
          {
            name: "PPT 生成",
            icon: "slides",
            description: "自动生成演示文稿",
            status: "pending",
            delay: 300,
            toolCalls: [
              { tool: "PPTGenerator.selectTemplate", label: "选择模板", category: "ppt", paramLabel: "科技简约 / 深蓝渐变", result: "TechLaunch Pro", status: "pending", delay: 500 },
              { tool: "PPTGenerator.fillContent", label: "填充内容", category: "ppt", paramLabel: "方案文档 + 数据图表", result: "24 页内容填充完成", status: "pending", delay: 2400 },
              { tool: "PPTGenerator.export", label: "导出文件", category: "file", paramLabel: "AI_发布会_方案.pptx", result: "24 页, 8.2 MB", status: "pending", delay: 3800 },
            ],
          },
        ],
        detailSteps: [
          { type: "thinking", content: "用户偏好科技简约风——深蓝色调 + 少量渐变。核心挑战是把 18 页方案压成视觉化的 PPT，重点突出 Q4 增长曲线、产品亮点、议程时间线。要兼顾现场演示和独立阅读两种场景。", delay: 200 },
          { type: "planning", content: "1. 选择匹配的 PPT 模板\n2. 填充方案内容并自动排版\n3. 导出 .pptx 文件", delay: 1200 },
          { type: "tool", skillRef: 0, toolRef: 0, delay: 2200 },
          { type: "text", content: "选定了「TechLaunch Pro」模板，深蓝主色调搭配白色文字，非常适合科技发布会的氛围。开始智能排版...", delay: 4200 },
          { type: "tool", skillRef: 0, toolRef: 1, delay: 5500 },
          { type: "text", content: "24 页内容填充完成，包括封面、目录、5 个内容章节、数据可视化页和结束页。正在优化图片质量并导出...", delay: 9000 },
          { type: "tool", skillRef: 0, toolRef: 2, delay: 10500 },
          { type: "result", content: "PPT 制作完成，文件名 AI_发布会_方案.pptx（24 页 / 8.2 MB）。已自动生成 Q4 增长趋势图、产品矩阵图和议程时间线可视化。", delay: 13500 },
        ],
      },
      /* ── 8. 发送成果 ── */
      {
        id: "send-feishu",
        name: "发送成果",
        role: "交付",
        description: "发送给李总",
        status: "pending",
        dependencies: ["ppt-gen"],
        delay: 32000,
        skills: [
          {
            name: "飞书发送",
            icon: "send",
            description: "消息推送",
            status: "pending",
            delay: 200,
            toolCalls: [
              { tool: "Feishu.send", label: "发送给李总", category: "feishu", paramLabel: "方案 + PPT 已准备完毕，请查收", result: "已送达", status: "pending", delay: 500 },
            ],
          },
        ],
        detailSteps: [
          { type: "thinking", content: "产出物齐了，最后一步——飞书发给李总。消息要简洁专业，附文件 + 关键信息（页数、概要），方便快速决策。", delay: 200 },
          { type: "planning", content: "1. 编写消息正文\n2. 附加方案文档和 PPT\n3. 发送并确认送达", delay: 1000 },
          { type: "text", content: "消息已编辑好，正在发送方案和 PPT 文件...", delay: 1800 },
          { type: "tool", skillRef: 0, toolRef: 0, delay: 2500 },
          { type: "result", content: "已成功发送给李总。消息：「李总，下周 AI 产品发布会方案（18 页）和 PPT（24 页）已准备完毕，请查收。如有需要调整的地方随时告知。」附件已送达。", delay: 5000 },
        ],
      },
    ],
  },
  {
    type: "summary",
    delay: 38000,
    summaryItems: [
      "已从小张和小王处获取完整演示素材",
      "已生成 18 页发布会策划方案",
      "已制作 24 页演示 PPT（AI_发布会_方案.pptx）",
      "已通过飞书将方案和 PPT 发送给李总",
    ],
    summaryFooter: "如需修改方案内容或调整 PPT 细节，请随时告诉我。",
  },
  {
    type: "follow-up",
    delay: 42000,
    followUps: [
      "帮我把 PPT 翻译成英文版本",
      "修改方案中的预算，控制在 20 万以内",
      "生成一份发布会邀请函模板",
      "查看方案中的议程详细时间表",
    ],
  },
]
